// src/components/TournamentsV2.js

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  AnimatePresence,
  motion,
} from "framer-motion";

import LoadingSpinner from "./LoadingSpinner";
import CardPicker from "./CardPicker";
import { getSkinSrc } from "./SeasonalSkins";

const API_BASE =
  process.env.REACT_APP_API_URL || "";

const FONT =
  '"Fredoka", sans-serif';

/* =========================================================
   API
   ========================================================= */

async function apiRequest(
  path,
  options = {}
) {
  const token =
    localStorage.getItem("token");

  const response = await fetch(
    `${API_BASE}${path}`,
    {
      ...options,

      headers: {
        "Content-Type":
          "application/json",

        ...(token
          ? {
              Authorization:
                `Bearer ${token}`,
            }
          : {}),

        "X-TG-INITDATA":
          window.Telegram
            ?.WebApp
            ?.initData || "",

        ...(options.headers || {}),
      },
    }
  );

  const text =
    await response.text();

  let data = {};

  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = {};
    }
  }

  if (!response.ok) {
    const error = new Error(
      data?.error ||
        data?.message ||
        `HTTP_${response.status}`
    );

    error.status =
      response.status;

    error.data = data;

    throw error;
  }

  return data;
}

/* =========================================================
   HELPERS
   ========================================================= */

function svgFallback(name = "?") {
  const initial =
    (name?.[0] || "?")
      .toUpperCase();

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64">
      <rect width="100%" height="100%" fill="#444"/>
      <text
        x="50%"
        y="50%"
        dominant-baseline="middle"
        text-anchor="middle"
        fill="white"
        font-size="32"
        font-family="Fredoka, Arial, sans-serif"
        font-weight="700"
      >
        ${initial}
      </text>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function readSelectedCardSnapshot() {
  try {
    const raw =
      localStorage.getItem(
        "selectedCardSnapshot"
      );

    if (!raw) {
      return null;
    }

    const parsed =
      JSON.parse(raw);

    return parsed &&
      typeof parsed === "object"
      ? parsed
      : null;
  } catch {
    return null;
  }
}

function parseDate(value) {
  if (!value) {
    return null;
  }

  const date =
    new Date(value);

  return Number.isNaN(
    date.getTime()
  )
    ? null
    : date;
}

function formatDate(value) {
  const date =
    parseDate(value);

  if (!date) {
    return "—";
  }

  return date.toLocaleString();
}

function shortDate(value) {
  const date =
    parseDate(value);

  if (!date) {
    return "—";
  }

  return date.toLocaleDateString(
    undefined,
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }
  );
}

function truncate(
  value,
  max = 20
) {
  const text =
    String(value || "");

  if (text.length <= max) {
    return text;
  }

  return `${text.slice(
    0,
    max
  )}...`;
}

function toNum(
  value,
  fallback = 0
) {
  const n = Number(value);

  return Number.isFinite(n)
    ? n
    : fallback;
}

function normalizeRarity(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function rarityLabel(value) {
  const rarity =
    normalizeRarity(value);

  if (!rarity) {
    return "Any";
  }

  return (
    rarity.charAt(0).toUpperCase() +
    rarity.slice(1)
  );
}

function getTournamentPrizeIcon(item) {
  const type = String(
    item?.type ??
    item?.item_type ??
    item?.itemType ??
    ""
  )
    .trim()
    .toLowerCase()
    .replaceAll("-", "_")
    .replaceAll(" ", "_");

  const name = String(
    item?.name ??
    item?.display_name ??
    item?.displayName ??
    ""
  ).toLowerCase();

  const fullItemText = JSON.stringify(item || {}).toLowerCase();

  if (
    type === "card_pieces" ||
    type === "cardpieces" ||
    type === "card_piece" ||
    name.includes("card pieces") ||
    name.includes("card fragment")
  ) {
    return "/pieces.png";
  }

  const isCard =
    type === "card" ||
    type === "cards" ||
    type === "character" ||
    type === "player_card" ||
    type.includes("_card") ||
    type.includes("card_") ||
    name.includes(" card") ||
    name.startsWith("card ") ||
    fullItemText.includes('"type":"card"') ||
    fullItemText.includes('"item_type":"card"');

  if (isCard) {
    if (fullItemText.includes("legendary")) return "/legendary.png";
    if (fullItemText.includes("epic")) return "/epic.png";
    if (fullItemText.includes("rare")) return "/rare.png";
    return "/common.png";
  }

  switch (type) {
    case "runes":
      return "/runes.png";

      case "ton":
  return "/ton.svg";

    case "sparks":
      return "/sparks.png";

    case "booster":
      return "/energy.png";

    case "tapi":
      return "/icon_tapi.png";

    case "seasonal_accessory":
      return "/fragment.png";

    case "ticket":
    case "tickets":
      return "/ticket.png";

    case "ability":
      return "/fightlog.png";

    case "shield":
      return "/shield.png";

    case "spins":
      return "/fortuneicon.png";

    case "evolve":
      return "/evolveicon.png";

    default:
      return item?.image || item?.image_url || item?.icon || item?.icon_url || "/pack.png";
  }
}

function statusLabel(status) {
  switch (status) {
    case "SCHEDULED":
      return "Coming Soon";

    case "JOINING":
      return "Registration Open";

    case "WAITING":
      return "Waiting To Start";

    case "ACTIVE":
      return "Tournament Active";

    case "FINISHING":
      return "Finishing";

    case "COMPLETED":
      return "Completed";

    case "CANCELLED":
      return "Cancelled";

    default:
      return status || "Tournament";
  }
}

function statusColor(status) {
  switch (status) {
    case "JOINING":
      return "#82f18a";

    case "ACTIVE":
      return "#ffd160";

    case "WAITING":
      return "#7ddcff";

    case "SCHEDULED":
      return "#c0c7ff";

    case "COMPLETED":
      return "#e7e7e7";

    case "CANCELLED":
      return "#ff7777";

    default:
      return "#fff";
  }
}

function getErrorMessage(code) {
  const messages = {
    CARD_REQUIRED:
      "Please choose a Fighter first.",

    CARD_NOT_FOUND:
      "This Fighter could not be found.",

    INVALID_CARD_RARITY:
      "This Fighter's rarity is not allowed in this tournament.",

    CARD_ALREADY_USED_IN_TOURNAMENT:
      "This Fighter has already been used in a previous tournament.",

    NOT_ENOUGH_TICKETS:
      "You do not have enough Tickets.",

    TOURNAMENT_FULL:
      "This tournament is already full.",

    JOINING_CLOSED:
      "Registration for this tournament is closed.",

    ALREADY_JOINED:
      "You already joined this tournament.",

    TOURNAMENT_NOT_ACTIVE:
      "The tournament is not active right now.",

    NOT_A_PARTICIPANT:
      "You are not participating in this tournament.",

    MATCH_NOT_FOUND:
      "This battle could not be found.",

    MATCH_ALREADY_PLAYED:
      "This battle has already been played.",

    MATCH_NOT_AVAILABLE:
      "This battle is no longer available.",

    MATCH_DOES_NOT_BELONG_TO_USER:
      "This battle does not belong to you.",

    TOURNAMENT_NOT_FOUND:
      "Tournament not found.",
  };

  return (
    messages[String(code || "")] ||
    "Something went wrong. Please try again."
  );
}

/* =========================================================
   COUNTDOWN
   ========================================================= */

function TournamentCountdown({
  tournament,
  onExpire,
}) {
  const [now, setNow] =
    useState(Date.now());

  const [
    expiredTarget,
    setExpiredTarget,
  ] = useState(null);

  /*
   * IMPORTANTE:
   * Não pode existir return antes
   * dos hooks deste componente.
   */

  let target = null;
  let label = "";

  const status =
    tournament?.status ||
    null;

  switch (status) {
    case "SCHEDULED":
      target =
        tournament?.joiningStartsAt ||
        null;

      label =
        "Registration Starts In";
      break;

    case "JOINING":
      target =
        tournament?.joiningEndsAt ||
        null;

      label =
        "Registration Ends In";
      break;

    case "WAITING":
      target =
        tournament?.startsAt ||
        null;

      label =
        "Tournament Starts In";
      break;

    case "ACTIVE":
      target =
        tournament?.endsAt ||
        null;

      label =
        "Tournament Ends In";
      break;

    default:
      target = null;
      label = "";
      break;
  }

  const targetDate =
    parseDate(target);

  const targetMs =
    targetDate
      ? targetDate.getTime()
      : null;

  useEffect(() => {
    const timer =
      setInterval(() => {
        setNow(Date.now());
      }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  /*
   * Detecta a virada do countdown.
   *
   * Este hook SEMPRE é chamado,
   * independente do status atual.
   */
  useEffect(() => {
    if (
      !target ||
      targetMs == null
    ) {
      return;
    }

    if (
      now < targetMs
    ) {
      return;
    }

    if (
      expiredTarget ===
      target
    ) {
      return;
    }

    setExpiredTarget(
      target
    );

    if (
      typeof onExpire ===
      "function"
    ) {
      onExpire();
    }
  }, [
    now,
    target,
    targetMs,
    expiredTarget,
    onExpire,
  ]);

  /*
   * SOMENTE AGORA podem existir
   * returns condicionais.
   */

  if (!tournament) {
    return null;
  }

  if (
    !target ||
    !targetDate
  ) {
    return (
      <div
        style={{
          fontFamily: FONT,
          fontWeight: 700,
          fontSize: 16,

          color:
            statusColor(
              tournament.status
            ),

          textShadow:
            "1px 2px #000",
        }}
      >
        {statusLabel(
          tournament.status
        )}
      </div>
    );
  }

  const difference =
    Math.max(
      0,
      targetMs - now
    );

  const totalSeconds =
    Math.floor(
      difference / 1000
    );

  const days =
    Math.floor(
      totalSeconds / 86400
    );

  const hours =
    Math.floor(
      (totalSeconds % 86400) /
        3600
    );

  const minutes =
    Math.floor(
      (totalSeconds % 3600) /
        60
    );

  const seconds =
    totalSeconds % 60;

  return (
    <div
      style={{
        fontFamily: FONT,
        textAlign: "center",
      }}
    >
      <div
        style={{
          color: "#ffd16a",

          fontFamily: FONT,
          fontSize: 15,
          lineHeight: "18px",
          fontWeight: 700,

          textShadow:
            "1px 2px #000",

          marginBottom: 4,
        }}
      >
        {label}
      </div>

      <div
        style={{
          display: "flex",

          justifyContent:
            "center",

          alignItems:
            "center",

          gap: 6,

          color: "#fff",

          fontFamily: FONT,
          fontSize: 14,
          lineHeight: "17px",
          fontWeight: 700,

          textShadow:
            "1px 2px #000",
        }}
      >
        <img
          src="/newclock.png"
          alt=""
          style={{
            width: 24,
            height: 24,

            objectFit:
              "contain",
          }}
        />

        <span>
          {days}D{" "}

          {String(
            hours
          ).padStart(
            2,
            "0"
          )}
          H{" "}

          {String(
            minutes
          ).padStart(
            2,
            "0"
          )}
          M{" "}

          {String(
            seconds
          ).padStart(
            2,
            "0"
          )}
          S
        </span>
      </div>
    </div>
  );
}

/* =========================================================
   CHARACTER
   ========================================================= */

function FighterThumb({
  card,
  size = 90,
}) {
  const character =
    useMemo(() => {
      const raw =
        card?.character;

      if (!raw) {
        return null;
      }

      if (
        typeof raw ===
        "object"
      ) {
        return raw;
      }

      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    }, [card]);

  const layers =
    useMemo(() => {
      const list =
        Object.entries(
          character || {}
        );

      const order = [
        "background",
        "body",
        "pants",
        "shirt",
        "boots",
        "neck",
        "face",
        "eyes",
        "mouth",
        "head",
        "weapons",
      ];

      return list.sort(
        ([a], [b]) => {
          const ai =
            order.includes(a)
              ? order.indexOf(a)
              : order.length;

          const bi =
            order.includes(b)
              ? order.indexOf(b)
              : order.length;

          return ai - bi;
        }
      );
    }, [character]);

  return (
    <div
      style={{
        width: size,
        height: size,
        minWidth: size,

        position: "relative",
        overflow: "hidden",

        borderRadius: 10,

        background:
          "rgba(0,0,0,.25)",

        border:
          "2px solid rgba(116,61,25,.8)",

        boxSizing:
          "border-box",
      }}
    >
      {!layers.length ? (
        <div
          style={{
            width: "100%",
            height: "100%",

            display: "grid",
            placeItems:
              "center",

            fontFamily: FONT,
            fontWeight: 600,
            fontSize: 14,
            color: "#ccc",
          }}
        >
          Fighter
        </div>
      ) : (
        layers.map(
          ([part, raw]) => {
            const data =
              raw &&
              typeof raw ===
                "object"
                ? raw
                : {};

            const fallback =
              `/assets/${part}/${data.side}/${data.rarity}/${data.variant}_thumb.png`;

            const src =
              getSkinSrc(
                card
                  ?.seasonal_accessory
                  ?.season_name ||
                  card?.season_name ||
                  null,

                part,

                fallback
              );

            return (
              <img
                key={part}
                src={src}
                alt=""
                draggable={false}
                style={{
                  position:
                    "absolute",

                  inset: 0,

                  width: "100%",
                  height: "100%",

                  objectFit:
                    "contain",

                  pointerEvents:
                    "none",
                }}
              />
            );
          }
        )
      )}
    </div>
  );
}

/* =========================================================
   SECTION — SAME STRUCTURE AS INVENTORY
   ========================================================= */

function Section({
  title,
  subtitle,
  actionLabel,
  onAction,
  children,
  style,
}) {
  return (
    <section
      style={{
        marginLeft: -10,
        marginRight: -10,
        fontFamily: FONT,
        ...style,
      }}
    >
      {title ? (
        <div
          className="modal_title"
          style={{
            boxSizing: "border-box",
            textAlign: "center",
            marginTop: 0,
            fontFamily: FONT,
          }}
        >
          {title}

          {subtitle != null ? (
            <>
              {" "}
              ({subtitle})
            </>
          ) : null}

          {actionLabel ? (
            <button
              type="button"
              onClick={onAction}
              className="modal_bt"
            >
              {actionLabel}
            </button>
          ) : null}
        </div>
      ) : null}

      <div
        className="modal_body_4"
        style={{
          fontFamily: FONT,
          paddingTop: 15
        }}
      >
        {children}
      </div>
    </section>
  );
}

/* =========================================================
   TOP TABS
   ========================================================= */

function TournamentTabs({
  view,
  setView,
  joined,
  pendingCount,
}) {
  const tabs = [
    {
      key: "home",
      label: "HOME",
    },
   {
  key: "battles",
  label: "BATTLES",
  badge: pendingCount > 0 ? pendingCount : null,
},
    {
      key: "ranking",
      label: "RANK",
    },
    {
      key: "prizes",
      label: "PRIZES",
    },
    {
      key: "info",
      label: "INFO",
    },
  ];

  return (
    <div
      style={{
        display: "grid",

        gridTemplateColumns:
          "repeat(5, minmax(0, 1fr))",

        gap: 6,

        width: "100%",

        marginBottom: 14,

        fontFamily: FONT,
      }}
    >
      {tabs.map((tab) => {
        const active =
          view === tab.key;

        const disabled =
          tab.key ===
            "battles" &&
          !joined;

        return (
          <button
            key={tab.key}
            type="button"
            disabled={disabled}
            onClick={() => {
              if (disabled) {
                return;
              }

              setView(tab.key);
            }}
            style={{
              width: "100%",
              minWidth: 0,
              height: 52,

              margin: 0,
              padding: "0 2px",

              boxSizing:
                "border-box",

              borderRadius: 7,

              background: active
                ? "rgba(112,61,27,.98)"
                : "rgba(79,38,14,.93)",

              border: active
                ? "2px solid #a36432"
                : "2px solid #51260d",

              boxShadow: active
                ? "inset 0 1px 0 rgba(255,255,255,.20), inset 0 -3px 0 rgba(0,0,0,.25), 0 2px 0 rgba(0,0,0,.22)"
                : "inset 0 1px 0 rgba(255,255,255,.10), inset 0 -3px 0 rgba(0,0,0,.20)",

              color: "#fff",

              fontFamily: FONT,
              fontWeight: 700,

              fontSize: 13,
              lineHeight: "16px",

              textAlign:
                "center",

              textShadow:
                "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",

              opacity: disabled
                ? 0.35
                : active
                  ? 1
                  : 0.88,

              cursor: disabled
                ? "default"
                : "pointer",
            }}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

/* =========================================================
   HOME STAT
   ========================================================= */

function HomeStat({ value, label }) {
  return (
    <div
      style={{
        aspectRatio: "1 / 1",
        width: "100%",
        minWidth: 0,
        padding: "8px 5px",
        boxSizing: "border-box",
        backgroundImage: 'url("/v2/item.png")',
        backgroundRepeat: "no-repeat",
        backgroundSize: "100% 100%",
        backgroundPosition: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        fontFamily: FONT,
      }}
    >
      <div
        className="shadow1"
        style={{
          color: "#fff",
          fontFamily: FONT,
          fontWeight: 600,
          fontSize: 16,
          lineHeight: "22px",
        }}
      >
        {value}
      </div>

      <div
        className="shadow1"
        style={{
          marginTop: 4,
          color: "#ffdf5f",
          fontFamily: FONT,
          fontWeight: 600,
          fontSize: 11,
          lineHeight: "13px",
        }}
      >
        {label}
      </div>
    </div>
  );
}

/* =========================================================
   RULE
   ========================================================= */

function Rule({
  label,
  value,
}) {
  return (
    <div
      style={{
        backgroundImage:
          'url("/v2/item.png")',

        backgroundRepeat:
          "no-repeat",

        backgroundSize:
          "100% 100%",

        backgroundPosition:
          "center center",

        minHeight: 105,

        padding:
          "14px 10px",

        boxSizing:
          "border-box",

        display: "flex",

        flexDirection:
          "column",

        alignItems:
          "center",

        justifyContent:
          "center",

        textAlign:
          "center",

        fontFamily: FONT,
      }}
    >
      <div
        className="shadow1"
        style={{
          color:
            "rgba(255,255,255,.78)",

          fontFamily: FONT,

          fontWeight: 600,

          fontSize: 13,

          lineHeight: "16px",

          textTransform:
            "uppercase",
        }}
      >
        {label}
      </div>

      <div
        className="shadow1"
        style={{
          marginTop: 5,

          color: "#fff",

          fontFamily: FONT,

          fontWeight: 700,

          fontSize: 18,

          lineHeight: "21px",
        }}
      >
        {value}
      </div>
    </div>
  );
}

/* =========================================================
   SMALL CARD STAT
   ========================================================= */

function CardStat({
  label,
  value,
}) {
  const icon =
    label === "ATK"
      ? "/s_attack.png"
      : label === "DEF"
        ? "/s_defense.png"
        : label === "HP"
          ? "/s_health.png"
          : null;

  return (
    <div
      style={{
        display: "flex",

        alignItems:
          "center",

        justifyContent:
          "center",

        gap: 4,

        minWidth: 54,

        padding:
          "5px 7px",

        boxSizing:
          "border-box",

        fontFamily: FONT,
      }}
    >
      {icon ? (
        <img
          src={icon}
          alt=""
          style={{
            width: 15,
            height: 15,

            objectFit:
              "contain",
          }}
        />
      ) : null}

      <span
        className="shadow1"
        style={{
          color: "#fff",

          fontFamily: FONT,

          fontWeight: 600,

          fontSize: 14,
        }}
      >
        {toNum(value)}
      </span>
    </div>
  );
}

/* =========================================================
   MAIN
   ========================================================= */

export default function TournamentsV2({
  user,
  setUser,
  onNavigate,
  onClose,
}) {
  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    detailLoading,
    setDetailLoading,
  ] = useState(false);

  const [
    tournaments,
    setTournaments,
  ] = useState([]);

  const [
    selectedTournamentId,
    setSelectedTournamentId,
  ] = useState(null);

  const [
    detail,
    setDetail,
  ] = useState(null);

  const [
    view,
    setView,
  ] = useState("home");

  const [
    pickerOpen,
    setPickerOpen,
  ] = useState(false);

  const [
    selectedCard,
    setSelectedCard,
  ] = useState(null);

  const [
    confirmJoinOpen,
    setConfirmJoinOpen,
  ] = useState(false);

  const [
    joining,
    setJoining,
  ] = useState(false);

  const [
    fightingMatchId,
    setFightingMatchId,
  ] = useState(null);

  const [
    battleResult,
    setBattleResult,
  ] = useState(null);

  const [
    showError,
    setShowError,
  ] = useState(false);

  const [
    errorMessage,
    setErrorMessage,
  ] = useState("");

  /* =======================================================
     LOAD TOURNAMENTS
     ======================================================= */

  async function loadTournaments() {
    try {
      const data =
        await apiRequest(
          "/api/tournaments"
        );

      const list =
        Array.isArray(
          data?.tournaments
        )
          ? data.tournaments
          : [];

      setTournaments(list);

      setSelectedTournamentId(
        (current) => {
          if (
            current &&
            list.some(
              (tournament) =>
                String(
                  tournament.id
                ) ===
                String(current)
            )
          ) {
            return current;
          }

          const preferred =
            list.find(
              (tournament) =>
                tournament.status ===
                "JOINING"
            ) ||
            list.find(
              (tournament) =>
                tournament.status ===
                "ACTIVE"
            ) ||
            list.find(
              (tournament) =>
                tournament.status ===
                "WAITING"
            ) ||
            list.find(
              (tournament) =>
                tournament.status ===
                "SCHEDULED"
            ) ||
            list[0];

          return (
            preferred?.id ||
            null
          );
        }
      );
    } catch (error) {
      console.error(
        "[TOURNAMENT V2 LIST]",
        error
      );

      setErrorMessage(
        "Unable to load tournaments."
      );

      setShowError(true);
    } finally {
      setLoading(false);
    }
  }

  /* =======================================================
     LOAD DETAIL
     ======================================================= */

  async function loadDetail(
    tournamentId,
    showSpinner = true
  ) {
    if (!tournamentId) {
      setDetail(null);
      return;
    }

    try {
      if (showSpinner) {
        setDetailLoading(true);
      }

      const data =
        await apiRequest(
          `/api/tournaments/${tournamentId}`
        );

      setDetail(data);

      if (
        data?.myEntry?.card
      ) {
        setSelectedCard(
          data.myEntry.card
        );
      }
    } catch (error) {
      console.error(
        "[TOURNAMENT V2 DETAIL]",
        error
      );

      setErrorMessage(
  error?.data?.error ||
  error?.data?.message ||
  error?.message ||
  `HTTP_${error?.status || "UNKNOWN"}`
);

      setShowError(true);
    } finally {
      if (showSpinner) {
        setDetailLoading(false);
      }
    }
  }

  useEffect(() => {
    loadTournaments();
  }, []);

  useEffect(() => {
    if (
      !selectedTournamentId
    ) {
      return;
    }

    setView("home");

    setSelectedCard(null);

    loadDetail(
      selectedTournamentId
    );
  }, [
    selectedTournamentId,
  ]);

  /* =======================================================
     DERIVED
     ======================================================= */

  const tournament =
    detail?.tournament ||
    tournaments.find(
      (entry) =>
        String(entry.id) ===
        String(
          selectedTournamentId
        )
    ) ||
    null;

  const myEntry =
    detail?.myEntry ||
    null;

  const matches =
    Array.isArray(
      detail?.myMatches
    )
      ? detail.myMatches
      : [];

  const standings =
    Array.isArray(
      detail?.standings
    )
      ? detail.standings
      : [];

  const prizes =
    Array.isArray(
      detail?.prizes
    )
      ? detail.prizes
      : [];

  const myStanding =
    myEntry?.standing ||
    null;

  const pendingMatches =
    matches.filter(
      (match) =>
        match.status ===
        "pending"
    );

  const completedMatches =
    matches.filter(
      (match) =>
        match.status ===
        "completed"
    );

  const firstPrize =
    prizes.find(
      (prize) =>
        Number(
          prize.position
        ) === 1
    ) || prizes[0] || null;

  const progress =
    tournament &&
    tournament.totalMatches > 0
      ? Math.round(
          (
            tournament
              .completedMatches /
            tournament.totalMatches
          ) *
            100
        )
      : 0;

  /* =======================================================
     OPEN CARD PICKER DIRECTLY FROM JOIN
     ======================================================= */

  function openJoinFlow() {
    if (
      tournament?.status !==
      "JOINING"
    ) {
      return;
    }

    setPickerOpen(true);
  }

  /* =======================================================
     CARD PICKED

     CARD PICKER -> CONFIRM DIRECTLY
     ======================================================= */

  function handleCardSelected(
    params = {}
  ) {
    const snapshot =
      readSelectedCardSnapshot();

    if (params?.cardId) {
      try {
        localStorage.setItem(
          "selectedCardId",
          String(
            params.cardId
          )
        );
      } catch {}
    }

    setPickerOpen(false);

    if (!snapshot?.id) {
      setErrorMessage(
        "Could not read the selected Fighter. Please choose it again."
      );

      setShowError(true);

      return;
    }

    const requiredRarity =
      normalizeRarity(
        tournament?.allowedRarity
      );

    const selectedRarity =
      normalizeRarity(
        snapshot?.rarity
      );

    if (
      requiredRarity &&
      requiredRarity !==
        "any" &&
      selectedRarity !==
        requiredRarity
    ) {
      setSelectedCard(null);

      setErrorMessage(
        `This tournament only accepts ${rarityLabel(
          requiredRarity
        )} Fighters.`
      );

      setShowError(true);

      return;
    }

    setSelectedCard(snapshot);

    setConfirmJoinOpen(true);
  }

  /* =======================================================
     JOIN
     ======================================================= */

  async function handleConfirmJoin() {
    if (
      joining ||
      !selectedCard?.id ||
      !selectedTournamentId
    ) {
      return;
    }

    try {
      setJoining(true);

      const data =
        await apiRequest(
          `/api/tournaments/${selectedTournamentId}/join`,
          {
            method: "POST",

            body:
              JSON.stringify({
                cardId:
                  selectedCard.id,
              }),
          }
        );

      if (
        typeof setUser ===
          "function" &&
        data?.user
      ) {
        setUser(
          (previous) => ({
            ...(previous || {}),
            ...data.user,
          })
        );
      }

      setConfirmJoinOpen(false);

      await loadTournaments();

      await loadDetail(
        selectedTournamentId,
        false
      );

      setView("home");
    } catch (error) {
      console.error(
        "[TOURNAMENT V2 JOIN]",
        error
      );

      setConfirmJoinOpen(false);

      setErrorMessage(
        getErrorMessage(
          error?.message
        )
      );

      setShowError(true);
    } finally {
      setJoining(false);
    }
  }

  /* =======================================================
     MANUAL FIGHT
     ======================================================= */

  async function handleFight(match) {
  if (
    !match?.id ||
    fightingMatchId ||
    !selectedTournamentId
  ) {
    return;
  }

  try {
    setFightingMatchId(
      match.id
    );

    const data =
      await apiRequest(
        `/api/battle/tournaments/${selectedTournamentId}/matches/${match.id}/start`,
        {
          method: "POST",
          body:
            JSON.stringify({}),
        }
      );

    if (
      !data?.battleId ||
      !data?.opponent ||
      !data?.playSig
    ) {
      throw new Error(
        "INVALID_TOURNAMENT_BATTLE"
      );
    }

    onNavigate?.(
      "battle",
      {
        battleId:
          data.battleId,

        cardId:
          detail?.myEntry
            ?.cardId,

        tournamentId:
          selectedTournamentId,

        tournamentMatchId:
          match.id,

        initialOpponent:
          data.opponent,

        initialPlaySig:
          data.playSig,

        returnTo:
          "tournamentsV2",

        returnParams: {
          tournamentId:
            selectedTournamentId,

          initialView:
            "battles",
        },

        openedVia:
          "tournament",
      }
    );
  } catch (error) {
    console.error(
      "[TOURNAMENT BATTLE START]",
      error
    );

    setErrorMessage(
      getErrorMessage(
        error?.message
      )
    );

    setShowError(true);
  } finally {
    setFightingMatchId(
      null
    );
  }
}

async function refreshTournamentState() {
  if (
    !selectedTournamentId
  ) {
    return;
  }

  try {
    /*
     * Reload detail first because
     * GET /:id prepares the tournament:
     * generates matches, cancels,
     * finalizes, etc.
     */
    await loadDetail(
      selectedTournamentId,
      false
    );

    await loadTournaments();
  } catch (error) {
    console.error(
      "[TOURNAMENT AUTO REFRESH]",
      error
    );
  }
}
  /* =======================================================
     LOADING
     ======================================================= */

  if (loading) {
    return (
      <div
        className="shop-container"
        style={{
          minHeight: 450,

          display: "flex",

          alignItems:
            "center",

          justifyContent:
            "center",

          fontFamily: FONT,
        }}
      >
        <LoadingSpinner
          text="Loading Tournament"
          size={64}
        />
      </div>
    );
  }

  /* =======================================================
     PAGE
     ======================================================= */

  return (
  <div
    className="shop-container"
    style={{
      paddingTop: 0,
      marginBottom: 0,
      paddingBottom: 0,
      fontFamily: FONT,
      fontWeight: 600,
      color: "#fff",
    }}
  >
    <div
      style={{
        width: "100%",
        maxWidth: 450,
        margin: "0 auto",
      }}
    >
      {/* ===================================================
          HERO
          =================================================== */}

      <div
        style={{
          position: "relative",

          width:
            "calc(100% + 20px)",

          height: 145,

          marginLeft: -10,
          marginTop: -10,
          marginBottom: 12,

          overflow: "hidden",

          backgroundImage:
            "url('/tournamentbg2.png')",

          backgroundSize:
            "cover",

          backgroundPosition:
            "center",

          borderBottom:
            "3px solid #6c2d0c",

          boxSizing:
            "border-box",

          fontFamily: FONT,
        }}
      >
        <div
          style={{
            position:
              "absolute",

            inset: 0,

            background:
              "linear-gradient(180deg,rgba(0,0,0,.04),rgba(0,0,0,.38))",
          }}
        />

        <div
          style={{
            position:
              "absolute",

            inset: 0,

            padding:
              "12px 15px",

            boxSizing:
              "border-box",

            display: "flex",

            flexDirection:
              "column",

            alignItems:
              "center",

            justifyContent:
              "center",

            fontFamily: FONT,

            textAlign:
              "center",
          }}
        >
          <div
            style={{
              color:
                "#ffd373",

              fontFamily: FONT,
              fontWeight: 700,

              fontSize: 14,

              lineHeight:
                "17px",

              letterSpacing:
                1,

              textTransform:
                "uppercase",

              textShadow:
                "1px 2px #000",
            }}
          >
            Tournament
          </div>

          <div
            style={{
              marginTop: 1,

              color: "#fff",

              fontFamily: FONT,
              fontWeight: 700,

              fontSize: 24,

              lineHeight:
                "28px",

              textShadow:
                "-2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 2px 2px 0 #000",
            }}
          >
            {tournament?.name ||
              "Tournament"}
          </div>

          {tournament ? (
            <div
              style={{
                minWidth:
                  240,

                marginTop: 8,

                padding:
                  "7px 13px",

                borderRadius:
                  12,

                background:
                  "rgba(0,0,0,.58)",

                border:
                  "1px solid rgba(255,255,255,.08)",

                boxSizing:
                  "border-box",

                fontFamily: FONT,
              }}
            >
              <TournamentCountdown
  tournament={tournament}
  onExpire={
    refreshTournamentState
  }
/>
            </div>
          ) : null}
        </div>
      </div>

      {/* ===================================================
          MULTIPLE TOURNAMENT SELECTOR
          =================================================== */}

      {tournaments.length >
  1 ? (
  <div
    style={{
      width: "100%",
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 8,
      marginBottom: 12,
      fontFamily: FONT,
    }}
  >
          {tournaments
  .slice(0, 2)
  .map((item) => {
              const active =
                String(
                  item.id
                ) ===
                String(
                  selectedTournamentId
                );

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() =>
                    setSelectedTournamentId(
                      item.id
                    )
                  }
                  style={{
                    minWidth: 150,
                    minHeight: 52,

                    padding:
                      "6px 10px",

                    borderRadius:
                      9,

                    background:
                      active
                        ? "#75401d"
                        : "#3d1c0b",

                    border:
                      active
                        ? "2px solid #b76a2e"
                        : "2px solid #56250d",

                    color: "#fff",

                    fontFamily:
                      FONT,

                    fontWeight:
                      700,

                    cursor:
                      "pointer",
                  }}
                >
                  <div
                    style={{
                      fontFamily:
                        FONT,

                      fontWeight:
                        700,

                      fontSize:
                        16,

                      lineHeight:
                        "19px",
                    }}
                  >
                    {truncate(
                      item.name,
                      18
                    )}
                  </div>

                  <div
                    style={{
                      marginTop: 2,

                      fontFamily:
                        FONT,

                      fontWeight:
                        600,

                      fontSize:
                        12,

                      color:
                        statusColor(
                          item.status
                        ),
                    }}
                  >
                    {statusLabel(
                      item.status
                    )}
                  </div>
                </button>
              );
            }
          )}
        </div>
      ) : null}

      {!tournament ? (
        <Section>
          <div
            style={{
              padding:
                "30px 10px",

              textAlign:
                "center",

              fontFamily: FONT,

              fontSize: 18,
            }}
          >
            There are no tournaments available right now.
          </div>
        </Section>
      ) : detailLoading ? (
        <div
          style={{
            minHeight: 320,

            display: "flex",

            alignItems:
              "center",

            justifyContent:
              "center",

            fontFamily: FONT,
          }}
        >
          <LoadingSpinner
            text="Loading Tournament"
            size={60}
          />
        </div>
      ) : detail ? (
        <>
          {/* ===============================================
              TABS
              =============================================== */}

          <TournamentTabs
            view={view}
            setView={setView}
            joined={
              Boolean(myEntry)
            }
            pendingCount={
              pendingMatches.length
            }
          />

          {/* ===============================================
              HOME
              =============================================== */}

          {view === "home" ? (
            <>
              <div
                style={{
                  display: "grid",

                 gridTemplateColumns:
  "repeat(4, minmax(0, 1fr))",

                  gap: 9,

                  marginBottom: 14,

                  fontFamily: FONT,

                }}
              >
                <HomeStat
                  value={`${tournament.participants}/${tournament.maxParticipants}`}
                  label="Players"
                />

                <HomeStat
                  value={
                    tournament.ticketPrice >
                    0
                      ? tournament.ticketPrice
                      : "FREE"
                  }
                  label={
                    tournament.ticketPrice >
                    0
                      ? "Tickets"
                      : "Entry"
                  }
                />

                {myEntry ? (
  <>
    <HomeStat
      value={myStanding ? `#${myStanding.position}` : "-"}
      label="My Rank"
    />

    <HomeStat
      value={myStanding?.wins || 0}
      label="My Wins"
    />
  </>
) : (
  <>
    <HomeStat
      value={
        tournament.allowedRarity === "any"
          ? "ANY"
          : rarityLabel(tournament.allowedRarity)
      }
      label="Rarity"
    />

    <HomeStat
      value="1v1"
      label="Round Robin"
    />
  </>
)}
              </div>

              {/* ===========================================
    USER NOT JOINED
    =========================================== */}

{!myEntry ? (
  <>
    <Section title="Tournament Entry">
      {/* FIRST PRIZE */}
      {firstPrize ? (
        <div
          className="forceinfo"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "9px 10px",
            fontFamily: FONT,
            textAlign: "left",
          }}
        >
          <img
            src={
              firstPrize?.prizeType === "ton"
                ? "/ton.svg"
                : getTournamentPrizeIcon(firstPrize?.item)
            }
            alt=""
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/pack.png";
            }}
            style={{
              width: 48,
              height: 48,
              minWidth: 48,
              objectFit: "contain",
            }}
          />

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              className="shadow1"
              style={{
                color: "#ffd168",
                fontWeight: 600,
                fontSize: 11,
                lineHeight: "14px",
                textTransform: "uppercase",
              }}
            >
              1st Place Prize
            </div>

            <div
              className="shadow1"
              style={{
                marginTop: 1,
                color: "#fff",
                fontWeight: 700,
                fontSize: 17,
                lineHeight: "20px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {firstPrize?.prizeType === "ton"
                ? `${firstPrize.tonAmount} TON`
                : firstPrize?.item?.name || "Reward"}
            </div>
          </div>

          {firstPrize?.prizeType !== "ton" &&
          Number(firstPrize.quantity) > 1 ? (
            <div
              className="shadow1"
              style={{
                color: "#ffd168",
                fontWeight: 700,
                fontSize: 16,
                flexShrink: 0,
              }}
            >
              ×{firstPrize.quantity}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* ENTRY INFO */}
      <div
        className="forceinfo"
        style={{
          padding: "10px",
          marginTop: 8,
          fontFamily: FONT,
          textAlign: "left",
        }}
      >
        <div
  style={{
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 8,
    textAlign: "center",
  }}
>
          <div>
            <div
              className="shadow1"
              style={{
                color: "#d5b28d",
                fontWeight: 600,
                fontSize: 11,
                lineHeight: "14px",
                textTransform: "uppercase",
              }}
            >
              Entry Cost
            </div>

            <div
              className="shadow1"
              style={{
                marginTop: 2,
                color: "#fff",
                fontWeight: 700,
                fontSize: 16,
              }}
            >
              {tournament.ticketPrice > 0
                ? `${tournament.ticketPrice} Ticket${tournament.ticketPrice === 1 ? "" : "s"}`
                : "FREE"}
            </div>
          </div>

          <div>
            <div
              className="shadow1"
              style={{
                color: "#d5b28d",
                fontWeight: 600,
                fontSize: 11,
                lineHeight: "14px",
                textTransform: "uppercase",
              }}
            >
              Fighter
            </div>

            <div
              className="shadow1"
              style={{
                marginTop: 2,
                color: "#fff",
                fontWeight: 700,
                fontSize: 16,
              }}
            >
              {tournament.allowedRarity === "any"
                ? "Any Rarity"
                : rarityLabel(tournament.allowedRarity)}
            </div>
          </div>

          <div style={{ textAlign: "center" }}>
  <div
    className="shadow1"
    style={{
      color: "#d5b28d",
      fontWeight: 600,
      fontSize: 11,
      lineHeight: "14px",
      textTransform: "uppercase",
    }}
  >
    Your Tickets
  </div>

  <div
    className="shadow1"
    style={{
      marginTop: 2,
      color: "#fff",
      fontWeight: 700,
      fontSize: 16,
    }}
  >
    {detail.userTickets ?? user?.tickets ?? 0}
  </div>
</div>
        </div>

        
      </div>

      {/* JOIN BUTTON */}
      <button
        type="button"
        className="game-button green-button"
        disabled={tournament.status !== "JOINING"}
        onClick={openJoinFlow}
        style={{
          width: "100%",
          minHeight: 46,
          marginTop: 9,
          fontFamily: FONT,
          fontWeight: 700,
          fontSize: 15,
          opacity: tournament.status === "JOINING" ? 1 : 0.45,
          cursor: tournament.status === "JOINING" ? "pointer" : "default",
        }}
      >
        {tournament.status === "JOINING"
          ? "CHOOSE FIGHTER & JOIN"
          : statusLabel(tournament.status)}
      </button>
    </Section>

    <Section
      title="How It Works"
      style={{ marginTop: 14 }}
    >
      <div
        className="forceinfo"
        style={{
          padding: "10px",
          fontFamily: FONT,
          textAlign: "left",
        }}
      >
        <div
          className="shadow1"
          style={{
            color: "#fff",
            fontWeight: 600,
            fontSize: 13,
            lineHeight: "19px",
          }}
        >
          Choose one Fighter and enter the tournament. Every participant fights
          every other participant once.
        </div>
      </div>
    </Section>
  </>
              ) : (
                /* =========================================
                   JOINED HOME
                   ========================================= */

               <Section title="Your Fighter">
  <div
    className="forceinfo"
    style={{
      display: "grid",
      gridTemplateColumns: "62px minmax(0, 1fr)",
      alignItems: "center",
      gap: 10,
      padding: "8px 9px",
      fontFamily: FONT,
      textAlign: "left",
    }}
  >
    <FighterThumb card={myEntry.card} size={62} />

    <div style={{ minWidth: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          whiteSpace: "nowrap",
        }}
      >
        <span
          className="shadow1"
          style={{
            color: "#9cff9c",
            fontWeight: 700,
            fontSize: 10,
            lineHeight: "12px",
          }}
        >
          ✓ YOU ARE IN
        </span>

        <span
          className="shadow1"
          style={{
            color: "#ffd168",
            fontWeight: 700,
            fontSize: 9,
            lineHeight: "11px",
          }}
        >
          🔒 LOCKED
        </span>
      </div>

      <div
        className="shadow1"
        style={{
          marginTop: 2,
          color: "#ffdf68",
          fontWeight: 700,
          fontSize: 16,
          lineHeight: "18px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {myEntry?.card?.name || "Fighter"}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          marginTop: 5,
          whiteSpace: "nowrap",
        }}
      >
        <span
          className="shadow1"
          style={{
            color: "#fff",
            fontWeight: 600,
            fontSize: 10,
            lineHeight: "12px",
          }}
        >
          {rarityLabel(myEntry?.card?.rarity)} • Lv. {myEntry?.card?.level ?? 1}
        </span>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <img src="/s_attack.png" alt="" style={{ width: 13, height: 13, objectFit: "contain" }} />
            <span className="shadow1" style={{ color: "#fff", fontWeight: 700, fontSize: 11 }}>
              {toNum(myEntry?.card?.attack)}
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <img src="/s_defense.png" alt="" style={{ width: 13, height: 13, objectFit: "contain" }} />
            <span className="shadow1" style={{ color: "#fff", fontWeight: 700, fontSize: 11 }}>
              {toNum(myEntry?.card?.defense)}
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <img src="/s_health.png" alt="" style={{ width: 13, height: 13, objectFit: "contain" }} />
            <span className="shadow1" style={{ color: "#fff", fontWeight: 700, fontSize: 11 }}>
              {toNum(myEntry?.card?.health)}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>

  {tournament.status === "ACTIVE" && pendingMatches.length > 0 ? (
    <button
      type="button"
      className="game-button green-button"
      onClick={() => setView("battles")}
      style={{
        width: "100%",
        minHeight: 38,
        marginTop: 8,
        fontFamily: FONT,
        fontWeight: 700,
        fontSize: 12,
      }}
    >
      PLAY MY BATTLES ({pendingMatches.length})
    </button>
  ) : null}
</Section>
              )}

              {/* ===========================================
                  PROGRESS
                  =========================================== */}

             {tournament.totalMatches > 0 ? (
  <Section
    title="Tournament Progress"
    style={{ marginTop: 14 }}
  >
    <div
      className="forceinfo"
      style={{
        padding: "10px",
        fontFamily: FONT,
        textAlign: "left",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 8,
        }}
      >
        <div
          className="shadow1"
          style={{
            color: "#fff",
            fontFamily: FONT,
            fontWeight: 600,
            fontSize: 13,
            lineHeight: "16px",
          }}
        >
          Battles completed
        </div>

        <div
          className="shadow1"
          style={{
            color: "#ffd168",
            fontFamily: FONT,
            fontWeight: 700,
            fontSize: 16,
            lineHeight: "18px",
            flexShrink: 0,
          }}
        >
          {tournament.completedMatches}/{tournament.totalMatches}
        </div>
      </div>

      <div
        style={{
          width: "100%",
          height: 14,
          borderRadius: 8,
          overflow: "hidden",
          background: "#140503",
          border: "1px solid rgba(255,255,255,.08)",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            width: `${progress}%`,
            height: "100%",
            background: "linear-gradient(90deg,#bf1515,#fd8700)",
            transition: "width .25s ease",
          }}
        />
      </div>
    </div>
  </Section>
) : null}
            </>
          ) : null}

                            {/* ===============================================
              BATTLES
              =============================================== */}

          {view === "battles" ? (
            <>
              {/* ===========================================
                  PENDING BATTLES
                  =========================================== */}

              <Section
                title={`Pending Battles${
                  pendingMatches.length > 0
                    ? ` (${pendingMatches.length})`
                    : ""
                }`}
              >
                {pendingMatches.length === 0 ? (
                  <div className="shadow1"
                    style={{
                      paddingTop: 17,
                      paddingBottom: 10,
                      textAlign: "center",
                      color: "#fff",
                      fontFamily: FONT,
                      fontWeight: 600,
                      fontSize: 16,
                    }}
                  >
                    {tournament.status === "JOINING"
                      ? "Battles begin when the tournament starts."
                      : tournament.status === "WAITING"
                        ? "You are registered. Waiting for the tournament to start."
                        : tournament.status === "ACTIVE"
                          ? "You have no pending battles."
                          : "No pending battles."}
                  </div>
                ) : (
                  pendingMatches.map(
                    (match) => (
                      <BattleRow
                        key={match.id}
                        match={match}
                        tournament={tournament}
                        fighting={
                          String(
                            fightingMatchId
                          ) ===
                          String(
                            match.id
                          )
                        }
                        onFight={() =>
                          handleFight(
                            match
                          )
                        }
                      />
                    )
                  )
                )}
              </Section>

              {/* ===========================================
                  COMPLETED BATTLES
                  =========================================== */}

              <Section
                title={`Completed Battles${
                  completedMatches.length > 0
                    ? ` (${completedMatches.length})`
                    : ""
                }`}
                style={{
                  marginTop: 14,
                }}
              >
                {completedMatches.length === 0 ? (
                  <div className="shadow1"
                    style={{
                      padding: "10px 8px",
                      paddingTop: "18px",
                      textAlign: "center",
                      color: "#fff",
                      fontFamily: FONT,
                      fontWeight: 600,
                      fontSize: 16,
                      lineHeight: "22px",
                    }}
                  >
                    No completed battles yet.
                  </div>
                ) : (
                  completedMatches.map(
                    (match) => (
                      <BattleRow
                        key={match.id}
                        match={match}
                        tournament={tournament}
                      />
                    )
                  )
                )}
              </Section>
            </>
          ) : null}

                            {/* ===============================================
              RANKING
              =============================================== */}

          {view === "ranking" ? (
            <Section title="Tournament Ranking">
              {standings.length === 0 ? (
                <div
                  className="shadow1"
                  style={{
                    padding: "18px 8px",
                    textAlign: "center",
                    fontFamily: FONT,
                    fontWeight: 600,
                    fontSize: 16,
                    color: "#fff",
                  }}
                >
                  No participants yet.
                </div>
              ) : (
                standings.map((entry) => (
                  <div
                    key={entry.participantId}
                    className="forceinfo"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      padding: "8px 9px",
                      fontFamily: FONT,
                      textAlign: "left",
                    }}
                  >
                    {/* POSITION */}
                    <div
                      style={{
                        width: 34,
                        minWidth: 34,
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {entry.position <= 3 ? (
                        <img
                          src={`/${entry.position}.png`}
                          alt=""
                          style={{
                            width: 32,
                            height: 32,
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <div
                          className="shadow1"
                          style={{
                            color: "#fff",
                            fontFamily: FONT,
                            fontWeight: 700,
                            fontSize: 15,
                          }}
                        >
                          #{entry.position}
                        </div>
                      )}
                    </div>

                    {/* AVATAR */}
                    {entry.avatarUrl ? (
                      <img
                        src={entry.avatarUrl}
                        alt=""
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          e.currentTarget.nextElementSibling.style.display = "flex";
                        }}
                        style={{
                          width: 40,
                          height: 40,
                          minWidth: 40,
                          borderRadius: 8,
                          objectFit: "cover",
                          background: "#4b2a16",
                        }}
                      />
                    ) : null}

                    {/* AVATAR FALLBACK */}
                    <div
                      className="shadow1"
                      style={{
                        width: 40,
                        height: 40,
                        minWidth: 40,
                        display: entry.avatarUrl ? "none" : "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 8,
                        background: "rgba(69,35,15,.85)",
                        border: "2px solid rgba(106,58,26,.9)",
                        boxSizing: "border-box",
                        color: "#ffd168",
                        fontFamily: FONT,
                        fontWeight: 700,
                        fontSize: 19,
                      }}
                    >
                      {(entry.name?.[0] || "?").toUpperCase()}
                    </div>

                    {/* USER */}
                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontFamily: FONT,
                      }}
                    >
                      <div
                        className="shadow1"
                        style={{
                          color: "#ffd168",
                          fontFamily: FONT,
                          fontWeight: 700,
                          fontSize: 16,
                          lineHeight: "19px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {truncate(entry.name || "Player", 18)}
                      </div>

                      <div
                        className="shadow1"
                        style={{
                          marginTop: 1,
                          color: "#fff",
                          fontFamily: FONT,
                          fontWeight: 600,
                          fontSize: 12,
                          lineHeight: "15px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {truncate(entry.card?.name || "Fighter", 18)}
                      </div>
                    </div>

                    {/* SCORE */}
                    <div
                      style={{
                        minWidth: 60,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 5,
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src="/v2/trophy.png"
                        alt=""
                        style={{
                          width: 22,
                          height: 22,
                          objectFit: "contain",
                        }}
                      />

                      <div
                        className="shadow1"
                        style={{
                          color: "#fff",
                          fontFamily: FONT,
                          fontWeight: 700,
                          fontSize: 18,
                          lineHeight: "20px",
                        }}
                      >
                        {entry.wins || 0}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </Section>
          ) : null}


                   {/* ===============================================
              PRIZES
              =============================================== */}

          {view === "prizes" ? (
            <Section title="Tournament Prizes">
              {prizes.length === 0 ? (
                <div
                  className="shadow1"
                  style={{
                    padding: "18px 8px",
                    textAlign: "center",
                    fontFamily: FONT,
                    fontWeight: 600,
                    fontSize: 16,
                    color: "#fff",
                  }}
                >
                  No prizes have been configured.
                </div>
              ) : (
               prizes.map((prize) => {
  const isTon =
    prize?.prizeType === "ton";

  return (
    <div
      key={prize.id}
      className="forceinfo"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        padding: "8px 9px",
        fontFamily: FONT,
        textAlign: "left",
      }}
    >
      {/* POSITION */}
      <div
        style={{
          width: 34,
          minWidth: 34,
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {prize.position <= 3 ? (
          <img
            src={`/${prize.position}.png`}
            alt=""
            style={{
              width: 32,
              height: 32,
              objectFit: "contain",
            }}
          />
        ) : (
          <div
            className="shadow1"
            style={{
              color: "#fff",
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            #{prize.position}
          </div>
        )}
      </div>

      {/* PRIZE ICON */}
      <img
        src={getTournamentPrizeIcon(prize?.item)}
        alt=""
        onError={(e) => {
          e.currentTarget.onerror = null;
          e.currentTarget.src = "/pack.png";
        }}
        style={{
          width: 40,
          height: 40,
          minWidth: 40,
          objectFit: "contain",
          flexShrink: 0,
        }}
      />

      {/* PRIZE INFO */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          fontFamily: FONT,
        }}
      >
        <div
          className="shadow1"
          style={{
            color: "#fff",
            fontFamily: FONT,
            fontWeight: 700,
            fontSize: 16,
            lineHeight: "19px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {isTon
            ? "TON"
            : prize?.item?.name || "Reward"}
        </div>

        <div
          className="shadow1"
          style={{
            marginTop: 1,
            color: "#ffd168",
            fontFamily: FONT,
            fontWeight: 600,
            fontSize: 12,
            lineHeight: "15px",
          }}
        >
          Position {prize.position}
        </div>
      </div>

      {/* AMOUNT / QUANTITY */}
      <div
        className="shadow1"
        style={{
          minWidth: 60,
          flexShrink: 0,
          color: "#ffd168",
          fontFamily: FONT,
          fontWeight: 700,
          fontSize: 18,
          lineHeight: "20px",
          textAlign: "right",
        }}
      >
        {isTon
          ? `${prize.tonAmount} TON`
          : `×${prize.quantity}`}
      </div>
    </div>
  );
})
              )}
            </Section>
          ) : null}

                    {/* ===============================================
              INFO
              =============================================== */}

          {view === "info" ? (
            <div
              style={{
                width: "100%",
                fontFamily: FONT,
              }}
            >
              {/* ===========================================
                  TOURNAMENT RULES
                  =========================================== */}

              <div
                style={{
                  marginLeft: -10,
                  marginRight: -10,
                  width: "calc(100% + 20px)",
                  marginBottom: 14,
                  fontFamily: FONT,
                }}
              >
                <div
                  className="modal_title"
                  style={{
                    margin: 0,
                    boxSizing: "border-box",
                    textAlign: "center",
                    fontFamily: FONT,
                  }}
                >
                  Tournament Rules
                </div>

                <div className="modal_body_4">
                  <div
                    style={{
                      display: "grid",

                      gridTemplateColumns:
                        "repeat(2, minmax(0, 1fr))",

                      gap: 9,

                      fontFamily: FONT,
                    }}
                  >
                    <Rule
                      label="Format"
                      value="Round Robin"
                    />

                    <Rule
                      label="Card"
                      value="One Fighter"
                    />

                    <Rule
                      label="Rarity"
                      value={
                        tournament.allowedRarity ===
                        "any"
                          ? "Any"
                          : rarityLabel(
                              tournament.allowedRarity
                            )
                      }
                    />

                    <Rule
                      label="Unique Card"
                      value={
                        tournament.uniqueCardRequired
                          ? "Required"
                          : "No"
                      }
                    />

                    <Rule
                      label="Minimum"
                      value={`${tournament.minParticipants} Players`}
                    />

                    <Rule
                      label="Maximum"
                      value={`${tournament.maxParticipants} Players`}
                    />
                  </div>

                  {tournament.description ? (
                    <div
                      style={{
                        marginTop: 13,

                        paddingTop: 11,

                        borderTop:
                          "1px solid rgba(255,255,255,.13)",

                        color:
                          "#ead9c9",

                        fontFamily:
                          FONT,

                        fontWeight:
                          500,

                        fontSize: 14,

                        lineHeight:
                          "20px",

                        textAlign:
                          "center",
                      }}
                    >
                      {tournament.description}
                    </div>
                  ) : null}
                </div>
              </div>

              {/* ===========================================
                  SCHEDULE
                  =========================================== */}

              <div
                style={{
                  marginLeft: -10,
                  marginRight: -10,
                  width: "calc(100% + 20px)",
                  fontFamily: FONT,
                }}
              >
                <div
                  className="modal_title"
                  style={{
                    margin: 0,
                    boxSizing: "border-box",
                    textAlign: "center",
                    fontFamily: FONT,
                  }}
                >
                  Schedule
                </div>

                <div className="modal_body_4" style={{paddingTop: 15,paddingBottom: 0}}>
                  <ScheduleLine
                    label="Registration Starts"
                    value={
                      tournament.joiningStartsAt
                    }
                  />

                  <ScheduleLine
                    label="Registration Ends"
                    value={
                      tournament.joiningEndsAt
                    }
                  />

                  <ScheduleLine
                    label="Tournament Starts"
                    value={
                      tournament.startsAt
                    }
                  />

                  <ScheduleLine
                    label="Tournament Ends"
                    value={
                      tournament.endsAt
                    }
                    last
                  />
                </div>
              </div>
              </div>
          ) : null}

        </>
      ) : null}

      {/* ===================================================
          CARD PICKER

          EXACT SAME BASIC FLOW AS ARENAS:
          CardPicker -> onSelected -> close
          =================================================== */}

      <div>
        {pickerOpen ? (
          <div
            className="modal_box"
            style={{
              zIndex: 9999,

              fontFamily: FONT,
            }}
          >
            <div
              className="modal_title"
              style={{
                fontFamily: FONT,
                fontWeight: 700,
                fontSize: 24,
              }}
            >
              Choose Your Fighter

              <button
                type="button"
                onClick={() =>
                  setPickerOpen(false)
                }
                aria-label="Close"
                className="modal_close"
              />
            </div>

            <div
              style={{
                fontFamily: FONT,
              }}
            >
              <CardPicker
  user={user}

  onClose={() =>
    setPickerOpen(false)
  }

  onNavigate={onNavigate}

  openedVia="tournament"

  initialRarity={
    tournament?.allowedRarity &&
    tournament.allowedRarity !== "any"
      ? tournament.allowedRarity
      : "all"
  }

  onSelected={(params) => {
    handleCardSelected(params);
  }}
/>
            </div>
          </div>
        ) : null}
      </div>

      {/* ===================================================
          CONFIRM JOIN
          =================================================== */}

      <div>
        {confirmJoinOpen &&
        selectedCard ? (
          <div
            className="modal_box"
            style={{
              zIndex:
                10000,

              fontFamily:
                FONT,
            }}
          >
            <div
              className="modal_title"
              style={{
                fontFamily:
                  FONT,

                fontWeight:
                  700,

                fontSize: 24,
              }}
            >
              Confirm Entry

              <button
                type="button"
                className="modal_close"
                onClick={() => {
                  if (!joining) {
                    setConfirmJoinOpen(
                      false
                    );
                  }
                }}
              />
            </div>

            <motion.div
              className="modal_body"
              initial={{
                scale: 0.85,
                opacity: 0,
              }}
              animate={{
                scale: 1,
                opacity: 1,
              }}
              exit={{
                scale: 0.92,
                opacity: 0,
              }}
              style={{
                fontFamily:
                  FONT,
              }}
            >
              <div
                className="modal_inside"
                style={{
                  fontFamily:
                    FONT,

                  textAlign:
                    "center",

                  padding: 18,
                }}
              >
                <div
                  style={{
                    display:
                      "flex",

                    justifyContent:
                      "center",

                    marginBottom:
                      12,
                  }}
                >
                  <FighterThumb
                    card={
                      selectedCard
                    }
                    size={120}
                  />
                </div>

                <div className="shadow2"
                  style={{
                    color:
                      "#fff",

                    fontFamily:
                      FONT,

                    fontWeight:
                      600,

                    fontSize: 25,

                    lineHeight:
                      "29px",
                  }}
                >
                  {selectedCard.name ||
                    "Fighter"}
                </div>

                <div className="shadow1"
                  style={{
                    marginTop: 4,

                    color: "#ddd",

                    fontFamily:
                      FONT,

                    fontWeight:
                      600,

                    fontSize: 16,
                  }}
                >
                  {rarityLabel(
                    selectedCard.rarity
                  )}

                  {" • "}

                  Level{" "}
                  {selectedCard.level ??
                    1}
                </div>

              <div
  className="forceinfo"
  style={{
    width: "100%",
    marginTop: 12,
    marginBottom: 18,
    padding: "12px 10px",
    boxSizing: "border-box",
  }}
>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 18,
      width: "100%",
    }}
  >
    <CardStat label="ATK" value={selectedCard.attack} />
    <CardStat label="DEF" value={selectedCard.defense} />
    <CardStat label="HP" value={selectedCard.health} />
  </div>
</div>

                <div className="shadow1"
                  style={{
                    marginTop: 0,

                    color:
                      "#e1d4ca",

                    fontFamily:
                      FONT,

                    fontWeight:
                      600,

                    fontSize: 18,

                    lineHeight:
                      "23px",
                  }}
                >
                  This Fighter will be locked for the entire tournament and cannot be changed after joining.
                </div>

                <div className="info"
                  style={{
                    marginTop: 16,

                    fontFamily:
                      FONT,
                  }}
                >
                  <div
                    style={{
                      color:
                        "#c9ad92",

                      fontFamily:
                        FONT,

                      fontWeight:
                        600,

                      fontSize: 14,
                    }}
                  >
                    Entry Cost
                  </div>

                  <div
                    style={{
                      marginTop: 3,

                      color:
                        "#ffd168",

                      fontFamily:
                        FONT,

                      fontWeight:
                        700,

                      fontSize: 23,
                    }}
                  >
                    {tournament.ticketPrice >
                    0
                      ? `${tournament.ticketPrice} Ticket${tournament.ticketPrice === 1 ? "" : "s"}`
                      : "FREE"}
                  </div>
                </div>
              </div>

              <div
                className="modal_footer"
                style={{
                  fontFamily:
                    FONT,
                }}
              >
                <button
                  type="button"
                  className="game-button green-button"
                  disabled={joining}
                  onClick={
                    handleConfirmJoin
                  }
                  style={{
                   marginRight: 10,
                    opacity:
                      joining
                        ? 0.55
                        : 1,
                  }}
                >
                  {joining
                    ? "JOINING..."
                    : "JOIN NOW"}
                </button>

                <button
                  type="button"
                  className="game-button red-button"
                  disabled={joining}
                  onClick={() =>
                    setConfirmJoinOpen(
                      false
                    )
                  }
                >
                  CANCEL
                </button>
              </div>
            </motion.div>
          </div>
        ) : null}
      </div>

      
      {/* ===================================================
          ERROR
          =================================================== */}

      <div>
        {showError ? (
          <div
            className="modal_box"
            style={{
              zIndex:
                11000,

              fontFamily:
                FONT,
            }}
          >
            <div
              className="modal_title"
              style={{
                fontFamily:
                  FONT,

                fontWeight:
                  700,

                fontSize: 24,
              }}
            >
              OOPS!

              <button
                type="button"
                className="modal_close"
                onClick={() =>
                  setShowError(false)
                }
              />
            </div>

            <motion.div
              className="modal_body"
              initial={{
                scale: 0.85,
                opacity: 0,
              }}
              animate={{
                scale: 1,
                opacity: 1,
              }}
              style={{
                fontFamily:
                  FONT,
              }}
            >
              <div
                className="modal_inside"
                style={{
                  textAlign:
                    "center",

                  padding: 20,

                  fontFamily:
                    FONT,
                }}
              >
                <img
                  src="/finger.png"
                  alt=""
                  style={{
                    height: 75,

                    marginBottom:
                      12,

                    transform:
                      "rotate(180deg)",
                  }}
                />

                <div className="info"
                  style={{
                    color: "#fff",

                    fontFamily:
                      FONT,

                    fontWeight:
                      600,

                    fontSize: 16,

                    lineHeight:
                      "24px",
                  }}
                >
                  {errorMessage}
                </div>
              </div>

              <div
                className="modal_footer"
              >
                <button
                  type="button"
                  className="game-button red-button"
                  onClick={() =>
                    setShowError(false)
                  }
                  style={{
                    minHeight:
                      50,

                    fontFamily:
                      FONT,

                    fontWeight:
                      700,

                    fontSize: 17,
                  }}
                >
                  CLOSE
                </button>
              </div>
            </motion.div>
          </div>
        ) : null}
            </div>
    </div>
  </div>
  );
}

/* =========================================================
   BATTLE ROW
   ========================================================= */

function BattleRow({
  match,
  tournament,
  fighting = false,
  onFight,
}) {
  const opponent =
    match?.opponent || {};

  const card =
    opponent?.card ||
    match?.opponentCard ||
    {};

  const completed =
    match?.status === "completed";

  const cancelled =
    match?.status === "cancelled";

  const canFight =
    match?.status === "pending" &&
    tournament?.status === "ACTIVE";

  const won =
    completed &&
    String(
      match?.result || ""
    ).toLowerCase() === "win";

  const opponentName =
  opponent?.name ||
  opponent?.username ||
  match?.opponentName ||
  "Opponent";

  return (
    <div
      className="forceinfo"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        fontFamily: FONT,
        textAlign: "left",
        padding: 10
      }}
    >
      {/* AVATAR */}
{opponent?.avatarUrl ? (
  <img
    src={opponent.avatarUrl}
    alt=""
    onError={(e) => {
      e.currentTarget.style.display = "none";
      e.currentTarget.nextElementSibling.style.display = "flex";
    }}
    style={{
      width: 40,
      height: 40,
      minWidth: 40,
      borderRadius: 8,
      objectFit: "cover",
      background: "#4b2a16",
      flexShrink: 0,
    }}
  />
) : null}

<div
  className="shadow1"
  style={{
    width: 40,
    height: 40,
    minWidth: 40,
    display: opponent?.avatarUrl ? "none" : "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
    background: "rgba(69,35,15,.85)",
    border: "2px solid rgba(106,58,26,.9)",
    boxSizing: "border-box",
    color: "#ffd168",
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: 19,
    flexShrink: 0,
  }}
>
  {(opponentName?.[0] || "?").toUpperCase()}
</div>

      {/* NAME / CARD */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          fontFamily: FONT,
        }}
      >
        <div
          style={{
            color: "#ffd168",

            fontFamily: FONT,
            fontWeight: 600,

            fontSize: 16,
            lineHeight: "20px",
            marginTop: -5,

            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {truncate(
            opponentName,
            20
          )}
        </div>

        <div
          style={{
            marginTop: 2,

            color: "#fff",

            fontFamily: FONT,
            fontWeight: 600,

            fontSize: 13,
            lineHeight: "16px",

            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {truncate(
            card?.name ||
              "Fighter",
            20
          )}
        </div>
      </div>

      {/* RESULT / ACTION */}
      <div
        style={{
          minWidth: 82,

          flexShrink: 0,

          textAlign: "right",

          fontFamily: FONT,
        }}
      >
        {completed ? (
          <div
            style={{
              color: won
                ? "#78eb82"
                : "#f08c8c",

              fontFamily: FONT,
              fontWeight: 600,

              fontSize: 14,
              lineHeight: "20px",
            }}
          >
            {won
              ? "VICTORY"
              : "DEFEAT"}
          </div>
        ) : cancelled ? (
          <div
            style={{
              color: "#bbb",

              fontFamily: FONT,
              fontWeight: 600,

              fontSize: 14,
            }}
          >
            EXPIRED
          </div>
        ) : canFight ? (
          <button
            type="button"
            className="game-button green-button"
            disabled={fighting}
            onClick={onFight}
            style={{
              minWidth: 82,
              minHeight: 40,

              margin: 0,
              padding: "5px 8px",

              fontFamily: FONT,
              fontWeight: 600,

              fontSize: 14,

              opacity:
                fighting
                  ? 0.55
                  : 1,
            }}
          >
            {fighting
              ? "..."
              : "FIGHT"}
          </button>
        ) : (
          <div
            style={{
              color: "#bbb",

              fontFamily: FONT,
              fontWeight: 600,

              fontSize: 14,
            }}
          >
            WAITING
          </div>
        )}
      </div>
    </div>
  );
}

/* =========================================================
   SCHEDULE LINE
   ========================================================= */

function ScheduleLine({
  label,
  value,
  last = false,
}) {
  return (
    <div
      style={{
        minHeight: 48,

        display: "flex",

        alignItems:
          "center",

        justifyContent:
          "space-between",

        gap: 4,

        boxSizing:
          "border-box",

        borderBottom: last
          ? "none"
          : "1px solid rgba(255,255,255,.10)",

        fontFamily:
          FONT,
      }}
    >
      <div className="shadow1"
        style={{
          flex: 1,

          color: "#d5b28d",

          fontFamily:
            FONT,

          fontWeight:
            600,

          fontSize: 16,
          textAlign: "left",

          lineHeight:
            "20px",
        }}
      >
        {label}:
      </div>

      <div className="shadow1"
        style={{
          color: "#fff",

          fontFamily:
            FONT,

          fontWeight:
            600,

          fontSize: 15,

          lineHeight:
            "20px",

          textAlign:
            "right",
        }}
      >
        {formatDate(value)}
      </div>
    </div>
  );
}